import React, { useContext, useMemo } from 'react';
import { TrackAssetContextType, TrackAssetProviderProps } from './types';
import useDragTrackAsset from './useDragTrackAsset';
import useResizeTrackAsset from './useResizeTrackAsset';

const TrackAssetContext = React.createContext<
  TrackAssetContextType | undefined
>(undefined);

const DEFAULT_DRAG_BOUNDS = {
  left: 0,
  right: Infinity,
};

const DEFAULT_SIZE_BOUNDS = {
  maxWidth: Infinity,
  minWidth: 0,
};

const DEFAULT_POSITION = { x: 0, y: 0 };

export const TrackAssetProvider: React.FC<TrackAssetProviderProps> = ({
  children,
  dragBounds = DEFAULT_DRAG_BOUNDS,
  id,
  position = DEFAULT_POSITION,
  pxPerSec,
  sizeBounds = DEFAULT_SIZE_BOUNDS,
  width,
  ...props
}) => {
  const {
    dragging,
    handleDrag,
    handleDragStart,
    handleDragStop,
  } = useDragTrackAsset({
    ...props,
    assetId: id,
  });

  const {
    resizing,
    handleResize,
    handleResizeStart,
    handleResizeStop,
  } = useResizeTrackAsset({
    ...props,
    assetId: id,
  });

  return (
    <TrackAssetContext.Provider
      value={useMemo(
        () => ({
          containerId: `track-${id}-asset-container`,
          dragging,
          dragBounds,
          position,
          pxPerSec,
          resizing,
          sizeBounds,
          handleDrag,
          handleDragStart,
          handleDragStop,
          handleResize,
          handleResizeStart,
          handleResizeStop,
          id,
          width,
        }),
        [
          dragBounds,
          dragging,
          handleDrag,
          handleDragStart,
          handleDragStop,
          handleResize,
          handleResizeStart,
          handleResizeStop,
          id,
          position,
          pxPerSec,
          resizing,
          sizeBounds,
          width,
        ],
      )}
    >
      {children}
    </TrackAssetContext.Provider>
  );
};

export function useTrackAssetContext() {
  const context = useContext(TrackAssetContext);

  if (context === undefined) {
    throw new Error(
      'useTrackAssetContxt must be used within TrackAssetProvider',
    );
  }

  return context;
}
